// Rules a player's own phone pays out (domain/arcade/plays): when today's answers reach the
// player's daily goal, record `reward.earned` once and say so. Check-ins pay where they happen.
import { useEffect } from 'react'
import type { Person } from '../../shared/events'
import { useToast } from '../app/toast'
import { record, stableId } from '../data/events'
import { dailyGoalDue } from '../domain/arcade/plays'
import { t } from '../strings/en'
import type { useArcade } from './useArcade'

/**
 * Goals already paid from this app instance (the event can take a moment to land). Other instances
 * can pay the same goal too: the event's id is the same for a person and day, so it lands once.
 */
const sent = new Set<string>()

export function useDailyGoalReward(person: Person, arcade: ReturnType<typeof useArcade>) {
  const { events, day, rules, settings } = arcade
  const goal = settings?.learn.goal
  // `settings` is null for someone who doesn't play, so they have no goal.
  const due = goal !== undefined && dailyGoalDue(events, person.id, day, goal, rules)
  useEffect(() => {
    const key = `${person.id}|${day}`
    if (!due || sent.has(key)) return
    sent.add(key)
    void (async () => {
      const id = await stableId(`daily|${person.id}|${day}`, Date.parse(`${day}T00:00:00Z`))
      await record('reward.earned', { trigger: 'daily', ref: day, plays: rules.daily.plays, day }, person.id, Date.now(), id)
      useToast.getState().show(t.plays.goalDone(rules.daily.plays), 3000)
    })()
  }, [due, person.id, day, rules.daily.plays])
}
